import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Form, Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box, IconButton } from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';

import Yup from '../../utils/yup';
import simpletaskService from '../../service/simpletask.service';
import masterDataService from '../../service/masterData.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

import useMenus from '../../context/MenuContext';
import { useLoader } from '../../provider/LoaderProvider';
import FormFields from './FormFields';
import { FormButtonPanelUI } from '../Interface';
import FormTabs from './FormTabs';
import { generateAutocompleteItems } from '../../utils/helper';
import { TaskReportOptions } from '../../config/taskReport';
import useUsers from '../../context/UsersContext';

export const TaskReportForm = ({ selectedTask, setShowDialog, onReportAdd }) => {
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { getUser, user } = useUsers();
  const { showLoader, hideLoader } = useLoader();
  const [submenu, setSubmenu] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [autocompleteData, setAutocompleteData] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    type: [],
    status: [],
  });

  const def = {
    simpleTaskId: null,
    timeSpent: '',
    me: '',
    typeId: null,
    description: '',
    createdBy: user.userId,
    statusId: null,
    taskReportAttachment: [],
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();
    setDefaultValues(def);
  }, []);

  useEffect(() => {
    setSubmenu(getMenuSubMenuId('simpleTask'));
  }, [menus]);

  useEffect(() => {
    if (submenu) initDefaultValues();
  }, [submenu]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          typeId: autocompleteData?.type?.defaultValue ?? null,
          statusId: autocompleteData?.status?.defaultValue ?? null,
        };
      });

      setDefaultSelectedValues((prev) => {
        return {
          ...prev,
          type: autocompleteData?.type?.defItem,
          status: autocompleteData?.status?.defItem,
        };
      });
    }
  }, [autocompleteData]);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .typeToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    showLoader();

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          type: resp[0],
          status: resp[1],
        };

        setAutocompleteData(aData);

        const acOptions = {
          type: generateAutocompleteItems(aData.type.typeItems, 'itemName', 'itemId'),
          status: generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(defaultSelectedValues, selectedTask)) {
        valuesToForm();
      } else {
        setInitValues(defaultValues);
      }
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    simpleTaskId: Yup.string().required(),
    timeSpent: Yup.number().required().positive(),
    me: Yup.string(),
    typeId: Yup.string().required(),
    description: Yup.string().max(500),
    statusId: Yup.string().required(),
  });

  const valuesToForm = () => {
    if (selectedTask) {
      const { simpleTaskId, typeId, statusId } = selectedTask;

      setInitValues({
        ...defaultValues,
        simpleTaskId,
        createdBy: user.userId,
        typeId: typeId ?? selectedTask.simpleTaskTypeId,
        statusId,
      });
    } else {
      setInitValues(defaultValues);
    }
  };

  const handleInsertObject = (values, { resetForm }) => {
    showLoader();

    const formData = new FormData();
    formData.append('data', JSON.stringify(values));

    if (values.taskReportAttachment) {
      for (const file of values.taskReportAttachment) {
        formData.append('files', file);
      }
    }

    simpletaskService
      .createTaskReport(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        resetForm();
        if (selectedTask && onReportAdd) {
          setShowDialog(false);
          onReportAdd(data);
        }
      })
      .finally(() => {
        hideLoader();
      });
  };

  return (
    <div className={selectedTask ? '' : 'bg-gray-100 h-full w-full pt-5'}>
      <div className={selectedTask ? 'mt-5' : 'h-auto bg-white py-5 px-5 max-w-[650px]  mx-auto rounded-lg'}>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleInsertObject}
          enableReinitialize
        >
          {({ values, setFieldValue }) => {
            const handleUploadFile = (files) => {
              const filesWithId = files.map((file) => {
                file.id = uuidv4();
                return file;
              });

              const existingAttachments = Array.isArray(values.taskReportAttachment) ? values.taskReportAttachment : [];

              setFieldValue('taskReportAttachment', [...existingAttachments, ...filesWithId]);
            };

            return (
              <Form>
                <FormFields selectedTask={selectedTask} autocompleteOptions={autocompleteOptions} user={user} />
                <FormTabs handleUploadFile={handleUploadFile} />
                <Box
                  display="flex"
                  justifyContent="center"
                  gap={3}
                  alignItems="center"
                  position="sticky"
                  bottom={0}
                  backgroundColor="#fff"
                  zIndex={10}
                >
                  <Box>
                    <FormButtonPanelUI />
                  </Box>
                  <Box>
                    <IconButton
                      component="label"
                      sx={{
                        background: '#f59e0b',
                        borderRadius: '0.5rem',
                        color: 'white',
                        '&:hover': {
                          background: '#d97706',
                        },
                      }}
                    >
                      <AttachmentIcon />
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={(e) => {
                          handleUploadFile(Array.from(e.target.files));

                          addNotification({
                            content: 'Sikeres fájl hozzáadás!',
                            type: NotificationType.SUCCESS,
                          });
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};
